import type { AppContextEntry } from './types'
import type { PageContextDescriptor } from './types'
import { getPageContextDescriptor } from './pageContextRegistry'
import { clampAppContextEntries } from './clamp'

export interface RouteBaselineBranch {
  name: string
  protected: boolean
}

export interface RouteBaselineInput {
  /** Matched route path pattern (e.g. `/plant/equipment/sites/:siteId`). */
  routePattern: string
  /** Concrete pathname (e.g. `/plant/equipment/sites/urn%3Asite%3A1`). */
  pathname: string
  /** Raw location search including `?`, or empty. */
  search: string
  /**
   * Active ontology branch. Omitted while the registry row is still loading —
   * the request header already carries the UUID, so a placeholder name here
   * would only invent one.
   */
  branch?: RouteBaselineBranch
  /**
   * Path-param values from the matched route (`useParams()`). Keys are
   * the param names without `:`.
   */
  params: Record<string, string | undefined>
}

/**
 * The model reasons in branch names; the header already carries the UUID.
 * Protected is the part that changes how a write should be treated.
 */
export function formatBranchAppContextValue(branch: RouteBaselineBranch): string {
  return branch.protected ? `${branch.name} (protected)` : branch.name
}

/**
 * Humanize a route pattern into a short Page label.
 * `/plant/equipment/sites/:siteId` → `Plant / Equipment / Sites`
 * `/graph` → `Graph`
 * `/` → `Work orders`
 */
export function pageLabelFromPattern(routePattern: string): string {
  if (routePattern === '/' || routePattern === '') return 'Work orders'
  if (routePattern === '*') return 'Not found'
  // Path-based explorer deep links — keep the Page label stable.
  if (routePattern.startsWith('/dashboards/templates')) return 'Dashboards / Templates'
  if (routePattern.startsWith('/dashboards')) return 'Dashboards'
  if (routePattern.startsWith('/documents/templates')) return 'Documents / Templates'
  if (routePattern.startsWith('/documents')) return 'Documents'
  if (routePattern.startsWith('/docs')) return 'Documents'
  const segments = routePattern
    .split('/')
    .filter((s) => s.length > 0 && !s.startsWith(':') && s !== '*')
  if (segments.length === 0) return routePattern
  return segments
    .map((s) =>
      s
        .split('-')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')
    )
    .join(' / ')
}

/**
 * Route-derived baseline snapshot: Page + Branch + path params + search params.
 * Returns an empty list when the route is declared `none` or unknown.
 */
export function produceRouteBaselineEntries(input: RouteBaselineInput): AppContextEntry[] {
  const descriptor: PageContextDescriptor | undefined = getPageContextDescriptor(input.routePattern)
  if (descriptor === undefined || descriptor === 'none') {
    return []
  }

  const entries: AppContextEntry[] = []
  // Global frame first (Page, Branch) — cheapest deictic anchors.
  entries.push({ key: 'Page', value: pageLabelFromPattern(input.routePattern) })
  if (input.branch !== undefined) {
    entries.push({ key: 'Branch', value: formatBranchAppContextValue(input.branch) })
  }

  // Path params in declaration order (Object key order from useParams is
  // insertion order matching the pattern's `:param` sequence in practice).
  for (const [name, value] of Object.entries(input.params)) {
    if (value === undefined || value === '') continue
    let decoded = value
    try {
      decoded = decodeURIComponent(value)
    } catch {
      /* keep raw */
    }
    entries.push({ key: name, value: decoded })
  }

  // Search params — stable alphabetical key order so the offered list is
  // deterministic across renders for the same URL.
  if (input.search.length > 1) {
    const qs = new URLSearchParams(
      input.search.startsWith('?') ? input.search.slice(1) : input.search
    )
    const keys = [...new Set([...qs.keys()])].sort()
    for (const key of keys) {
      const values = qs.getAll(key)
      const first = values[0]
      if (first === undefined) continue
      entries.push({
        key,
        value: values.length === 1 ? first : values.join(', ')
      })
    }
  }

  return clampAppContextEntries(entries)
}
